import React from 'react'
import { Route, Switch } from 'react-router'
import { connect } from 'react-redux'

import loadable from 'components/utils/lazyImport'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  BUSINESS_TRAVELLER_NO_HEADER_ROUTES,
  BUSINESS_TRAVELLER_SUPPORT_HEADER_ROUTES,
} from 'businessTraveller/routes/businessTravellerRoutes'
import config from 'constants/config'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP } from 'agentHub/routes/AgentHubRoutes'
import { useAppSelector } from 'hooks/reduxHooks'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { CHECKOUT_HEADER_PATHS } from './constants'

const HeaderMain = loadable(() => import(/* webpackChunkName: 'HeaderMain' */ 'components/App/Header/HeaderMain/HeaderMain'), <div />)
const HeaderCheckout = loadable(() => import(/* webpackChunkName: 'HeaderCheckout' */ 'components/App/Header/HeaderCheckout'), <div />)
const HeaderTripPlannerImmersive = loadable(() => import(/* webpackChunkName: 'TripPlannerImmersiveHeader' */ 'tripPlanner/components/ImmersiveHeader'), <div />)
const BusinessTravellerSupportHeader = loadable(() => import(/* webpackChunkName: 'BusinessTravellerSupportHeader' */ 'businessTraveller/components/BusinessTravellerSupportHeader'), <div />)
const BusinessTravellerGuestHeader = loadable(() => import(/* webpackChunkName: 'BusinessTravellerGuestHeader' */ 'businessTraveller/components/BusinessTravellerGuestHeader'), <div />)
const AgentHubGuestHeader = loadable(() => import(/* webpackChunkName: 'AgentHubGuestHeader' */ 'agentHub/components/AgentHubGuestHeader'), <div />)

const routesWithNoHeader = [
  '/:regionCode/gift-card/:code',
  '/:regionCode/trip-planner/trip',
  '/:regionCode/trip-planner/curated/:id',
  '/:regionCode/trip-planner/public-trip/:id',
  '/:regionCode/trip-planner/generate-trip',
  '/:regionCode/search/map',
  '/:regionCode/search/homes-and-villas/map',
  '/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher',
  `/:regionCode${LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP}`,
  ...BUSINESS_TRAVELLER_NO_HEADER_ROUTES,
]

const stickySearchHeaderPaths = [
  '/:regionCode/hotels',
  '/:regionCode/tours',
  '/:regionCode/cruises',
  '/:regionCode/car-hire',
  '/:regionCode/experiences',
  '/:regionCode/flights',
  '/:regionCode/homes-and-villas',
  '/:regionCode/trip-planner',
  '/:regionCode/trip-planner/about',
  '/:regionCode/trip-planner/curated',
  '/:regionCode',
]

const desktopAndLargerOnlyHeaderPaths = [
  '/:regionCode/support',
]

interface Props {
  isLoggedIn: boolean;
  tripPlannerTripId?: string;
}

function HeaderRoutes(props: Props) {
  const { isLoggedIn, tripPlannerTripId } = props
  const { canEdit } = useTripPermissions(tripPlannerTripId)

  const agentHubAccountSetupComplete = useAppSelector(state => state.agentHub.agentHubAccount.account?.status === 'onboarded')

  return (
    <Switch>
      <Route path={routesWithNoHeader} />
      <Route path={CHECKOUT_HEADER_PATHS} component={HeaderCheckout} />
      {config.agentHub.isEnabled && !agentHubAccountSetupComplete && <Route component={AgentHubGuestHeader} />}
      {config.businessTraveller.currentAccountMode === 'business' && <Route exact path={BUSINESS_TRAVELLER_SUPPORT_HEADER_ROUTES} component={BusinessTravellerSupportHeader} />}
      {config.businessTraveller.currentAccountMode === 'business' && !isLoggedIn && <Route component={BusinessTravellerGuestHeader} />}
      {tripPlannerTripId && canEdit && <Route>
        <HeaderTripPlannerImmersive tripId={tripPlannerTripId} />
      </Route>}
      <Route exact path={stickySearchHeaderPaths}>
        <HeaderMain stickySearch />
      </Route>
      <Route exact path={desktopAndLargerOnlyHeaderPaths}>
        <CSSBreakpoint min="desktop">
          <HeaderMain />
        </CSSBreakpoint>
      </Route>
      <Route component={HeaderMain} />
    </Switch>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: selectLoggedIn(state),
    tripPlannerTripId: state.tripPlanner.currentTripId,
  }
}

export default connect(mapStateToProps)(HeaderRoutes)
